// @ts-check

/**
 * @file Provides the shared Card surface used by every list / detail
 * screen. Centralizes the `surface` background, `border` outline,
 * 12-radius pill shape, and the per-theme cached style key so each
 * screen can render the Card component instead of repeating the
 * same five style properties in 30+ places.
 */

import PropTypes from "prop-types"
import {memo, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"

import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {{children?: ReactNode, style?: object, testID: string, [key: string]: unknown}} CardProps
 */

/** @typedef {Record<string, never>} CardState */

/**
 * Shared Card surface — paints `colors.surface` with a `colors.border`
 * outline at radius 12 and the standard 16px padding. Pass extra
 * spacing or layout props through `style`; the shared base wins on
 * background, border, and radius so consumers can't accidentally
 * drift into a hardcoded grey again.
 * @augments {ShapeComponent<CardProps, CardState>}
 */
class Card extends ShapeComponent {
  static propTypes = {
    children: PropTypes.any,
    style: PropTypes.object,
    testID: PropTypes.string.isRequired
  }

  static defaultProps = {
    children: null,
    style: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the card surface.
   * @returns {ReactNode} Card content wrapped in the shared surface.
   */
  render() {
    // Destructure off `this.props` (not `this.p`) — `this.p` is a
    // fetching-object Proxy without `ownKeys`, so a `{...rest}` spread
    // silently drops every un-named prop. We need `rest` so an
    // `<Link asChild>` parent can forward `href` / `onPress` / `onClick`
    // / `role` to the underlying View.
    const {children, style, testID, ...rest} = this.props
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const viewStyle = useMemo(() => {
      const baseStyle = styles[`card-${themeName}`] ||= {
        backgroundColor: colors.surface,
        borderColor: colors.border,
        borderRadius: 12,
        borderWidth: 1,
        padding: 16
      }
      return style ? [baseStyle, style] : baseStyle
    }, [colors, style, themeName])

    return (
      <View
        {...rest}
        style={viewStyle}
        testID={testID}
      >
        {children}
      </View>
    )
  }
}

/** @type {NamedExoticComponent<CardProps>} */
const CardComponent = memo(shapeComponent(Card))

export default CardComponent
